import React, { useState, useEffect } from 'react';
import UiGiftCards from '@rcg/ui-react/lib/components/GiftCards';
import { useGlobal } from '@rcg/ui-react';
import { useLazyQuery } from '@apollo/client';
import { formatPrice } from '@rcg/pricing-format';
import Dictionary from '../../utils/dictionary';
import { GIFT_CARD_BALANCE_INQUIRY } from '../../operations/queries';
import commonUtils from '../../utils/common';
import {
  trackErrorGiftCard,
  trackremoveGiftCard,
  trackSuccesGiftCard,
} from '../../utils/analytics/browseTracking';

const GIFTCARD_VALIDATION = 'air:giftcardvalidation';

const GiftCards = ({
  extraData,
  holdFlightData,
  showErrorGiftCardsBanner,
  setLoader,
  setRemainingBalance,
  setGiftCard,
  setDisableCreditCardForm,
  hideErrorBanners,
  maximumNumberOfGiftCards,
}) => {
  const {
    renderContent,
    data: { localizedDayjs },
  } = useGlobal();
  const [giftCardBalancePromise, setGiftCardBalancePromise] = useState(null);
  const [giftCardAdded, setGiftCardAdded] = useState([]);
  const { returnFlight } = extraData;
  const { priceChange } = holdFlightData;
  const amountValue = priceChange
    ? priceChange.newTotal.value
    : returnFlight.cabin.totalPrice.value;
  const [currentBalanceToPay, setCurrentBalanceToPay] = useState(amountValue);

  const dictionaries = {
    giftCardBalanceApplied: renderContent(
      Dictionary.giftCardBalanceApplied.key,
      Dictionary.giftCardBalanceApplied.defaultValue,
    ),
    giftCardBalanceAppliedAddAnother: renderContent(
      Dictionary.giftCardBalanceAppliedAddAnother.key,
      Dictionary.giftCardBalanceAppliedAddAnother.defaultValue,
    ),
    applyRoyalGiftCardTitle: renderContent(
      Dictionary.applyRoyalGiftCardTitle.key,
      Dictionary.applyRoyalGiftCardTitle.defaultValue,
    ),
    applyRoyalGiftCardSubTitle: renderContent(
      Dictionary.applyRoyalGiftCardSubTitle.key,
      Dictionary.applyRoyalGiftCardSubTitle.defaultValue,
    ),
    applyGiftCard: renderContent(
      Dictionary.applyGiftCard.key,
      Dictionary.applyGiftCard.defaultValue,
    ),
    addedGiftCardendingCard: renderContent(
      Dictionary.addedGiftCardendingCard.key,
      Dictionary.addedGiftCardendingCard.defaultValue,
    ),
    addedGiftCardWillBeCharged: renderContent(
      Dictionary.addedGiftCardWillBeCharged.key,
      Dictionary.addedGiftCardWillBeCharged.defaultValue,
    ),
    addedGiftCardRemainingBalance: renderContent(
      Dictionary.addedGiftCardRemainingBalance.key,
      Dictionary.addedGiftCardRemainingBalance.defaultValue,
    ),
    addedGiftCardRemoveCard: renderContent(
      Dictionary.addedGiftCardRemoveCard.key,
      Dictionary.addedGiftCardRemoveCard.defaultValue,
    ),
    addedGiftCardAddAnotherGiftCard: renderContent(
      Dictionary.addedGiftCardAddAnotherGiftCard.key,
      Dictionary.addedGiftCardAddAnotherGiftCard.defaultValue,
    ),
    giftCardInformationFormEmptyCardNumber: renderContent(
      Dictionary.giftCardInformationFormEmptyCardNumber.key,
      Dictionary.giftCardInformationFormEmptyCardNumber.defaultValue,
    ),
    giftCardInformationFormInvalidCardNumber: renderContent(
      Dictionary.giftCardInformationFormInvalidCardNumber.key,
      Dictionary.giftCardInformationFormInvalidCardNumber.defaultValue,
    ),
    giftCardInformationFormCardNumberErrorRequired: renderContent(
      Dictionary.giftCardInformationFormCardNumberErrorRequired.key,
      Dictionary.giftCardInformationFormCardNumberErrorRequired.defaultValue,
    ),
    giftCardInformationFormExpireMonthErrorRequired: renderContent(
      Dictionary.giftCardInformationFormExpireMonthErrorRequired.key,
      Dictionary.giftCardInformationFormExpireMonthErrorRequired.defaultValue,
    ),
    giftCardInformationFormExpireYearErrorRequired: renderContent(
      Dictionary.giftCardInformationFormExpireYearErrorRequired.key,
      Dictionary.giftCardInformationFormExpireYearErrorRequired.defaultValue,
    ),
    giftCardInformationFormSecurityCodeErrorRequired: renderContent(
      Dictionary.giftCardInformationFormSecurityCodeErrorRequired.key,
      Dictionary.giftCardInformationFormSecurityCodeErrorRequired.defaultValue,
    ),
    giftCardInformationFormSecurityCodeErrorOnlyNumbers: renderContent(
      Dictionary.giftCardInformationFormSecurityCodeErrorOnlyNumbers.key,
      Dictionary.giftCardInformationFormSecurityCodeErrorOnlyNumbers.defaultValue,
    ),
    giftCardInformationFormCardNumberErrorInvalid: renderContent(
      Dictionary.giftCardInformationFormCardNumberErrorInvalid.key,
      Dictionary.giftCardInformationFormCardNumberErrorInvalid.defaultValue,
    ),
    giftCardInformationFormCreditCardAlreadyExistErrorTitle: renderContent(
      Dictionary.giftCardInformationFormCreditCardAlreadyExistErrorTitle.key,
      Dictionary.giftCardInformationFormCreditCardAlreadyExistErrorTitle.defaultValue,
    ),
    giftCardInformationFormCreditCardAlreadyExistErrorDescription: renderContent(
      Dictionary.giftCardInformationFormCreditCardAlreadyExistErrorDescription.key,
      Dictionary.giftCardInformationFormCreditCardAlreadyExistErrorDescription.defaultValue,
    ),
    giftCardInformationFormCardNumberLabel: renderContent(
      Dictionary.giftCardInformationFormCardNumberLabel.key,
      Dictionary.giftCardInformationFormCardNumberLabel.defaultValue,
    ),
    giftCardInformationFormCardNumberPlaceholder: renderContent(
      Dictionary.giftCardInformationFormCardNumberPlaceholder.key,
      Dictionary.giftCardInformationFormCardNumberPlaceholder.defaultValue,
    ),
    giftCardInformationFormExpirationDateLabel: renderContent(
      Dictionary.giftCardInformationFormExpirationDateLabel.key,
      Dictionary.giftCardInformationFormExpirationDateLabel.defaultValue,
    ),
    giftCardInformationFormSecurityCodeLabel: renderContent(
      Dictionary.giftCardInformationFormSecurityCodeLabel.key,
      Dictionary.giftCardInformationFormSecurityCodeLabel.defaultValue,
    ),
    giftCardInformationFormSecurityCodePlaceholder: renderContent(
      Dictionary.giftCardInformationFormSecurityCodePlaceholder.key,
      Dictionary.giftCardInformationFormSecurityCodePlaceholder.defaultValue,
    ),
    giftCardInformationFormMonthArray: localizedDayjs.localeData().monthsShort(),
    giftCardInformationFormMonthDefaultValue: renderContent(
      Dictionary.airFormReservationFieldMonthLabel.key,
      Dictionary.airFormReservationFieldMonthLabel.defaultValue,
    ),
    giftCardInformationFormYearDefaultValue: renderContent(
      Dictionary.airFormReservationFieldYearLabel.key,
      Dictionary.airFormReservationFieldYearLabel.defaultValue,
    ),
    giftCardInformationFormSecurityCodeErrorThreeDigitsOnly: renderContent(
      Dictionary.giftCardInformationFormSecurityCodeErrorThreeDigitsOnly.key,
      Dictionary.giftCardInformationFormSecurityCodeErrorThreeDigitsOnly.defaultValue,
    ),
  };

  const [
    executeAplyGiftCardQuery,
    {
      called: applyGiftCardCalled,
      loading: applyGiftCardLoading,
      data: applyGiftCardData,
      error: applyGiftCardError,
    },
  ] = useLazyQuery(GIFT_CARD_BALANCE_INQUIRY, { fetchPolicy: 'network-only' });

  const calculateAmounts = (balance, balanceToPay, giftcards, isAddingGiftCard) => {
    const chargedAmount = balance <= balanceToPay ? balance : balanceToPay;
    const remainingBalanceAmount =
      chargedAmount === balance ? 0 : Math.round((balance - chargedAmount) * 100) / 100;
    const balanceToPayResult =
      balanceToPay - balance <= 0 ? 0 : Math.round((balanceToPay - balance) * 100) / 100;
    const giftCardLength = isAddingGiftCard ? giftcards.length + 1 : giftcards.length;
    const addAnotherGiftCard = balanceToPayResult > 0 && giftCardLength < maximumNumberOfGiftCards;
    return {
      chargedAmount,
      remainingBalanceAmount,
      balanceToPayResult,
      addAnotherGiftCard,
    };
  };

  const handleApplyGiftCardApiCall = (giftCardInfo) => {
    const { cardNumber, securityCode, expiryMonth, expiryYear } = giftCardInfo;
    const queryParams = {
      variables: {
        accountNumber: cardNumber.replace(/\s+/g, ''),
        cvv2: securityCode,
        expirationDate: `${expiryMonth < 10 ? '0' : ''}${expiryMonth}${expiryYear}`,
      },
    };
    setLoader(true);
    executeAplyGiftCardQuery(queryParams);
    return new Promise((resolve, reject) => {
      setGiftCardBalancePromise({ resolve, reject, giftCardInfo });
    });
  };

  const errorHandler = (title, description) => {
    showErrorGiftCardsBanner(title, description);
  };

  const recalculateGiftCard = (giftCardsFiltered, currentBalanceToPayVar) => {
    let balanceToPayVar = currentBalanceToPayVar;
    const giftCardUiReactArray = [];
    giftCardsFiltered.forEach((giftcard) => {
      let addAnotherGiftCardVal = true;
      // eslint-disable-next-line no-param-reassign
      giftcard.addAnotherGiftCard = true;
      if (giftcard.chargedNotFormatted !== giftcard.available) {
        balanceToPayVar += +giftcard.chargedNotFormatted;
        // Recalculate charge and remaning balance
        const balance = giftcard.available;
        const { chargedAmount, remainingBalanceAmount, balanceToPayResult, addAnotherGiftCard } =
          calculateAmounts(balance, balanceToPayVar, giftCardsFiltered, false);
        balanceToPayVar = balanceToPayResult;
        addAnotherGiftCardVal = addAnotherGiftCard;

        // eslint-disable-next-line no-param-reassign
        giftcard.charged = formatPrice(+chargedAmount, giftcard.currency, true, true, true);
        // eslint-disable-next-line no-param-reassign
        giftcard.remainingBalance = formatPrice(
          +remainingBalanceAmount,
          giftcard.currency,
          true,
          true,
          true,
        );
        /* eslint-disable no-return-assign, no-param-reassign */
        giftcard.chargedNotFormatted = chargedAmount;
        giftcard.remainingBalanceNotFormatted = remainingBalanceAmount;
        giftcard.addAnotherGiftCard = addAnotherGiftCardVal;
        /* eslint-enable no-return-assign, no-param-reassign */
      }
      giftCardUiReactArray.push({
        cardNumber: giftcard.info.cardNumber,
        expiryMonth: giftcard.info.expiryMonth,
        expiryYear: giftcard.info.expiryYear,
        securityCode: giftcard.info.securityCode,
        balance: {
          charged: giftcard.charged,
          remainingBalance: giftcard.remainingBalance,
          addAnotherGiftCard: addAnotherGiftCardVal,
        },
      });
    });

    setGiftCardAdded(giftCardsFiltered);
    setGiftCard(giftCardsFiltered);
    setCurrentBalanceToPay(balanceToPayVar);
    setRemainingBalance(balanceToPayVar);
    setDisableCreditCardForm(balanceToPayVar === 0);
    return giftCardUiReactArray;
  };

  const handleRemoveGiftCard = (cardNumber) => {
    // Handle remove giftcar
    const giftCards = [...giftCardAdded];
    const giftCardsFiltered = giftCards.filter(
      (giftcard) => giftcard.info.cardNumber !== cardNumber,
    );
    const giftCardFound = giftCardAdded.find((giftcard) => giftcard.info.cardNumber === cardNumber);
    const currentBalanceToPayVar = currentBalanceToPay + giftCardFound.chargedNotFormatted;
    trackremoveGiftCard();
    return recalculateGiftCard(giftCardsFiltered, currentBalanceToPayVar);
  };

  useEffect(() => {
    // Handling response data
    if (applyGiftCardCalled && !applyGiftCardLoading && applyGiftCardData && !applyGiftCardError) {
      setLoader(false);
      hideErrorBanners();
      const { price, status } = applyGiftCardData.giftCardBalanceInquiry;
      const { currency, value } = price;
      const giftCards = [...giftCardAdded];
      const balance = value;
      if (balance === 0 || status === 'CLOSED') {
        showErrorGiftCardsBanner(
          renderContent(
            Dictionary.giftCardNoEnoughBalanceErrorTitle.key,
            Dictionary.giftCardNoEnoughBalanceErrorTitle.defaultValue,
          ),
          renderContent(
            Dictionary.giftCardNoEnoughBalanceErrorDescription.key,
            Dictionary.giftCardNoEnoughBalanceErrorDescription.defaultValue,
          ),
        );
        trackErrorGiftCard('error', GIFTCARD_VALIDATION);
      } else {
        const { chargedAmount, remainingBalanceAmount, balanceToPayResult, addAnotherGiftCard } =
          calculateAmounts(balance, currentBalanceToPay, giftCards, true);
        const giftCardInfoNew = { ...giftCardBalancePromise.giftCardInfo };
        setRemainingBalance(balanceToPayResult);
        const giftCardBalance = {
          available: balance,
          charged: formatPrice(+chargedAmount, currency, true, true, true),
          remainingBalance: formatPrice(+remainingBalanceAmount, currency, true, true, true),
          chargedNotFormatted: chargedAmount,
          remainingBalanceNotFormatted: remainingBalanceAmount,
          addAnotherGiftCard,
          info: giftCardInfoNew,
          currency,
        };

        giftCards.push(giftCardBalance);
        setCurrentBalanceToPay(balanceToPayResult);
        setDisableCreditCardForm(balanceToPayResult === 0);
        setGiftCardAdded(giftCards);
        setGiftCard(giftCards);
        giftCardInfoNew.balance = giftCardBalance;
        giftCardBalancePromise.resolve(giftCardInfoNew);
        trackSuccesGiftCard(chargedAmount);
      }
    }
    // Handling errors
    if (applyGiftCardError) {
      setLoader(false);
      let title;
      let description;
      if (commonUtils.findErrorCode(applyGiftCardError, 'INCORRECT_GIFT_CARD_REQUEST_INFO')) {
        title = renderContent(
          Dictionary.giftCardIncorrectGiftCardRequestInfoErrorTitle.key,
          Dictionary.giftCardIncorrectGiftCardRequestInfoErrorTitle.defaultValue,
        );
        description = renderContent(
          Dictionary.giftCardIncorrectGiftCardRequestInfoErrorDescription.key,
          Dictionary.giftCardIncorrectGiftCardRequestInfoErrorDescription.defaultValue,
        );
        trackErrorGiftCard('error', GIFTCARD_VALIDATION);
        showErrorGiftCardsBanner(title, description);
      } else if (commonUtils.findErrorCode(applyGiftCardError, 'GIFT_CARD_TECHNICAL_ISSUES')) {
        title = renderContent(
          Dictionary.giftCardIncorrectThechnicalIssueErrorTitle.key,
          Dictionary.giftCardIncorrectThechnicalIssueErrorTitle.defaultValue,
        );
        description = renderContent(
          Dictionary.giftCardIncorrectThechnicalIssueErrorDescription.key,
          Dictionary.giftCardIncorrectThechnicalIssueErrorDescription.defaultValue,
        );
        trackErrorGiftCard('Technical issue', GIFTCARD_VALIDATION);
        showErrorGiftCardsBanner(title, description);
      } else {
        title = renderContent(
          Dictionary.giftCardIncorrectThechnicalIssueNotMappedErrorTitle.key,
          Dictionary.giftCardIncorrectThechnicalIssueNotMappedErrorTitle.defaultValue,
        );
        description = renderContent(
          Dictionary.giftCardIncorrectThechnicalIssueNotMappedErrorDescription.key,
          Dictionary.giftCardIncorrectThechnicalIssueNotMappedErrorDescription.defaultValue,
        );
        trackErrorGiftCard('error', GIFTCARD_VALIDATION);
        showErrorGiftCardsBanner(title, description);
      }
    }
    // eslint-disable-next-line
  }, [applyGiftCardCalled, applyGiftCardLoading, applyGiftCardData, applyGiftCardError]);

  return (
    <>
      <UiGiftCards
        dictionaries={dictionaries}
        handleApplyGiftCardApiCall={handleApplyGiftCardApiCall}
        handleRemoveGiftCard={handleRemoveGiftCard}
        errorHandler={errorHandler}
      />
    </>
  );
};

export default GiftCards;
